import { useEffect, useState } from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { fetchMyArticleVote, recordHelpEvent } from "@/lib/help-analytics"

/**
 * Helpful / not-helpful vote widget for a help article (Task #2088).
 * Votes are tenant-scoped analytics events; one live vote per user per
 * article (server keeps only the latest).
 */
export function VCHelpVote({
  articleId,
  className,
}: {
  articleId: string
  className?: string
}) {
  const [vote, setVote] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setVote(null)
    void fetchMyArticleVote(articleId).then((v) => {
      if (!cancelled) setVote(v)
    })
    return () => {
      cancelled = true
    }
  }, [articleId])

  const cast = (v: "helpful" | "not_helpful") => {
    setVote(v)
    recordHelpEvent({ eventType: "article_vote", articleId, context: v })
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2",
        className,
      )}
      data-testid={`help-vote-${articleId}`}
    >
      <span className="text-xs text-muted-foreground">
        {vote ? "Thanks for the feedback!" : "Was this article helpful?"}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => cast("helpful")}
          aria-label="Helpful"
          data-testid="help-vote-up"
          className={cn(
            "rounded-md border p-1.5 transition-colors hover:bg-muted",
            vote === "helpful" && "border-primary bg-primary/10 text-primary",
          )}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => cast("not_helpful")}
          aria-label="Not helpful"
          data-testid="help-vote-down"
          className={cn(
            "rounded-md border p-1.5 transition-colors hover:bg-muted",
            vote === "not_helpful" && "border-destructive bg-destructive/10 text-destructive",
          )}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
